import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Activity, 
  Camera, 
  MapPin, 
  Calendar,
  RefreshCw,
  ChevronRight,
  User
} from 'lucide-react';
import { todayInTimezone } from '@/utils/dateUtils';
import { useLanguage } from '@/i18n/LanguageContext';

function ActivitiesPage() {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyTimezone, setCompanyTimezone] = useState('UTC');

  useEffect(() => {
    const loadCompanyTimezone = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings/company`, { withCredentials: true });
        const tz = res.data?.timezone || 'UTC';
        setCompanyTimezone(tz);
        setSelectedDate((prev) => prev || todayInTimezone(tz));
      } catch (e) {
        // ignore
      }
    };

    loadCompanyTimezone();
  }, []);

  const [selectedDate, setSelectedDate] = useState(todayInTimezone('UTC'));

  useEffect(() => {
    // Once we know the company timezone, set default date accordingly
    setSelectedDate(todayInTimezone(companyTimezone));
  }, [companyTimezone]);

  useEffect(() => {
    fetchActivities();
  }, [selectedDate]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activities/summary`, {
        params: { date: selectedDate },
        withCredentials: true
      });
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshotsClick = (staffId) => {
    navigate(`/admin/activities/screenshots/${staffId}?date=${selectedDate}`);
  };

  const handleGPSClick = (staffId) => {
    navigate(`/admin/activities/gps/${staffId}?date=${selectedDate}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-8 h-8 text-purple-600" />
            Activities
          </h2>
          <p className="text-gray-600 mt-1">Monitor staff screenshots and GPS tracking</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
              data-testid="date-picker"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={fetchActivities}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Activities Table */}
      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-purple-600" />
            Staff Activities
          </CardTitle>
          <CardDescription>
            Click on counts to view detailed activity data
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Activity className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg">No active staff found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Staff</TableHead>
                  <TableHead className="font-semibold text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Camera className="w-4 h-4 text-pink-600" />
                      Screenshots
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-center">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="w-4 h-4 text-indigo-600" />
                      GPS Tracking
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activities.map((activity) => (
                  <TableRow key={activity.staff_id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                          {activity.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{activity.name}</p>
                          <p className="text-sm text-gray-500">{activity.email}</p>
                          <p className="text-xs text-gray-400">{activity.department}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        onClick={() => handleScreenshotsClick(activity.staff_id)}
                        className="hover:bg-pink-50 group"
                        data-testid={`screenshots-btn-${activity.staff_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${activity.screenshot_count > 0 ? 'text-pink-600' : 'text-gray-400'}`}>
                            {activity.screenshot_count}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-pink-600 transition-colors" />
                        </div>
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        onClick={() => handleGPSClick(activity.staff_id)}
                        className="hover:bg-indigo-50 group"
                        data-testid={`gps-btn-${activity.staff_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${activity.gps_count > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                            {activity.gps_count}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                        </div>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ActivitiesPage;
